/**
 * Main layout component with sidebar navigation
 */

import { ReactNode, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts";
import { Icon } from "@/components/ui";
import { NotificationPanel } from "@/components/NotificationPanel";
import styles from "./MainLayout.module.css";

interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
}

interface MainLayoutProps {
  children: ReactNode;
  title: string;
  currentPath: string;
  onNavigate: (path: string) => void;
}

const navItems: NavItem[] = [
  {
    id: "dashboard",
    label: "Panel de Control",
    icon: "dashboard",
    path: "/dashboard",
  },
  { id: "clients", label: "Clientes", icon: "users", path: "/clients" },
  { id: "quotes", label: "Presupuestos", icon: "document", path: "/quotes" },
  { id: "treatments", label: "Plagas", icon: "bug", path: "/treatments" },
  {
    id: "workorders",
    label: "Órdenes de Trabajo",
    icon: "clipboard",
    path: "/workorders",
  },
  {
    id: "requests",
    label: "Solicitudes",
    icon: "fileText",
    path: "/requests",
  },
  {
    id: "invoicing",
    label: "Facturación",
    icon: "creditCard",
    path: "/invoicing",
  },
  { id: "inventory", label: "Inventario", icon: "package", path: "/inventory" },
  { id: "reports", label: "Informes", icon: "barChart", path: "/reports" },
];

/**
 * Main layout with sidebar navigation
 */
export function MainLayout({
  children,
  title,
  currentPath,
  onNavigate,
}: MainLayoutProps): React.ReactElement {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleNavigate = (path: string) => {
    onNavigate(path);
    closeSidebar();
  };

  const getUserInitials = () => {
    if (!user) return "?";
    const firstInitial = user.firstName?.[0] || "";
    const lastInitial = user.lastName?.[0] || "";
    return (
      (firstInitial + lastInitial).toUpperCase() || user.email[0].toUpperCase()
    );
  };

  return (
    <div className={styles.layout}>
      {/* Overlay para móvil */}
      <div
        className={`${styles.overlay} ${sidebarOpen ? styles.visible : ""}`}
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.open : ""}`}>
        <div className={styles.logo}>
          <h1 className={styles.logoTitle}>Biosam</h1>
          <p className={styles.logoSubtitle}>Admin CRM</p>
        </div>

        <nav className={styles.nav}>
          {navItems.map((item) => (
            <button
              key={item.id}
              className={`${styles.navItem} ${currentPath === item.path ? styles.active : ""}`}
              onClick={() => handleNavigate(item.path)}
            >
              <span className={styles.navIcon}>
                <Icon name={item.icon} size={20} />
              </span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className={styles.user}>
          <div className={styles.userAvatar}>{getUserInitials()}</div>
          <div className={styles.userInfo}>
            <p className={styles.userName}>
              {user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.email}
            </p>
            <p className={styles.userRole}>Admin</p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className={styles.main}>
        <header className={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              className={styles.menuToggle}
              onClick={toggleSidebar}
              title="Menú"
            >
              <Icon name="menu" size={24} />
            </button>
            <h2 className={styles.headerTitle}>{title}</h2>
          </div>
          <div className={styles.headerActions}>
            <button
              className={styles.notificationButton}
              onClick={() => setShowNotifications(!showNotifications)}
              title="Notificaciones"
            >
              <Icon name="bell" size={20} />
              {unreadCount > 0 && (
                <span className={styles.notificationBadge}>{unreadCount}</span>
              )}
            </button>
            {showNotifications && (
              <NotificationPanel
                onClose={() => setShowNotifications(false)}
                onNavigate={(path) => {
                  setShowNotifications(false);
                  onNavigate(path);
                }}
              />
            )}
            <button
              className={styles.userMenuButton}
              onClick={() => setShowUserMenu(!showUserMenu)}
              title="Menú de usuario"
            >
              <Icon name="moreVertical" size={20} />
            </button>
            {showUserMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "60px",
                  right: "32px",
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  padding: "8px",
                  minWidth: "150px",
                }}
              >
                <button
                  onClick={handleLogout}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "none",
                    background: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#dc2626",
                    borderRadius: "4px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fee2e2";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </header>

        <div className={styles.content}>{children}</div>
      </main>
    </div>
  );
}

export default MainLayout;
